<template>
  <div class="app-container finance-page">
    <finance-query-panel
      title="回款分析"
      description="回款按系统实际回款日期统计，可按真实签约年份、回款部门、人员、产品和回款方式分析。"
      :report-year.sync="reportYear"
      :query-params.sync="queryParams"
      :dashboard="dashboard"
      :loading="loading"
      @query="queryData"
      @reset="resetQuery"
    />
    <el-row :gutter="14">
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">{{ reportYear }}年月度实际回款</div>
          <finance-chart :option="monthlyOption" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">回款方式构成</div>
          <finance-chart :option="payTypeOption" />
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="14">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">部门回款排名</div>
          <finance-chart :option="departmentOption" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">个人回款 Top 10</div>
          <finance-chart :option="personalOption" />
        </el-card>
      </el-col>
    </el-row>
    <el-card shadow="never" class="table-card">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="部门与产品回款" name="fund">
          <finance-monthly-table :rows="dashboard.fundMonthlyRows" :dimension-columns="fundDimensions" :loading="loading" />
        </el-tab-pane>
        <el-tab-pane label="个人回款" name="personal">
          <finance-monthly-table :rows="dashboard.personalFundRows" :dimension-columns="personalDimensions" :loading="loading" />
        </el-tab-pane>
        <el-tab-pane label="签约统计" name="contract">
          <finance-monthly-table :rows="dashboard.contractMonthlyRows" :dimension-columns="contractDimensions" :loading="loading" />
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import FinanceChart from './components/FinanceChart'
import FinanceMonthlyTable from './components/FinanceMonthlyTable'
import FinanceQueryPanel from './components/FinanceQueryPanel'
import receivableFinanceMixin from './components/receivableFinanceMixin'
import { groupTotals, sumMonths } from './components/financeShared'

export default {
  name: 'FundAnalysis',
  financeScene: 'fund',
  components: { FinanceChart, FinanceMonthlyTable, FinanceQueryPanel },
  mixins: [receivableFinanceMixin],
  data () {
    return {
      activeTab: 'fund',
      fundDimensions: [
        { prop: 'contractYear', label: '签约年份', fixed: 'left', width: 100 },
        { prop: 'departmentName', label: '回款部门', fixed: 'left', minWidth: 150 },
        { prop: 'productCategory', label: '产品大类', minWidth: 160 },
        { prop: 'payType', label: '回款方式', width: 110 }
      ],
      personalDimensions: [
        { prop: 'userName', label: '回款人', fixed: 'left', minWidth: 120 },
        { prop: 'departmentName', label: '回款部门', fixed: 'left', minWidth: 150 },
        { prop: 'contractYear', label: '签约年份', width: 100 }
      ],
      contractDimensions: [{ prop: 'departmentName', label: '签约部门', fixed: 'left', minWidth: 150 }]
    }
  },
  computed: {
    monthlyOption () {
      return this.lineOption(sumMonths(this.dashboard.fundMonthlyRows))
    },
    payTypeOption () {
      const rows = groupTotals(this.dashboard.fundMonthlyRows, 'payType')
      return {
        tooltip: { trigger: 'item', formatter: params => `${params.name}<br/>${this.amount(params.value)} 元<br/>${params.percent}%` },
        legend: { type: 'scroll', bottom: 0 },
        series: [{ type: 'pie', radius: ['42%', '68%'], center: ['50%', '44%'], data: rows }]
      }
    },
    departmentOption () {
      return this.barOption(groupTotals(this.dashboard.fundMonthlyRows, 'departmentName'))
    },
    personalOption () {
      return this.barOption(groupTotals(this.dashboard.personalFundRows, 'userName').slice(0, 10))
    }
  },
  methods: {
    lineOption (data) {
      return {
        color: ['#22b573'], tooltip: { trigger: 'axis', valueFormatter: value => this.amount(value) },
        grid: { left: 24, right: 24, top: 24, bottom: 20, containLabel: true },
        xAxis: { type: 'category', data: Array.from({ length: 12 }, (item, index) => `${index + 1}月`) },
        yAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        series: [{ type: 'line', smooth: true, areaStyle: { opacity: 0.12 }, symbolSize: 7, data }]
      }
    },
    barOption (rows) {
      return {
        color: ['#4b86f8'], tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: value => this.amount(value) },
        grid: { left: 18, right: 24, top: 18, bottom: 16, containLabel: true },
        xAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        yAxis: { type: 'category', data: rows.map(item => item.name), axisLabel: { width: 90, overflow: 'truncate' } },
        series: [{ type: 'bar', barMaxWidth: 20, data: rows.map(item => item.value) }]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.finance-page { min-height: calc(100vh - 84px); background: #f5f7fa; }
.chart-card, .table-card { margin-bottom: 14px; }
.card-title { margin-bottom: 12px; color: #303133; font-size: 16px; font-weight: 600; }
</style>
